import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import SearchableDropdown from '../../common/SearchableDropdown';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack, TrendingUp, TrendingDown, EmojiEvents } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar, LinearProgress } from '@mui/material';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// ─── Theme ──────────────────────────────────────────────────────────────────
const PRIMARY      = '#1a237e';
const PRIMARY_MID  = '#283593';
const PRIMARY_DARK = '#0d1b6e';
const ACCENT_GREEN = '#1b5e20';
const ACCENT_RED   = '#b71c1c';
const ACCENT_AMBER = '#e65100';

// ─── Helpers ────────────────────────────────────────────────────────────────
const fmtAmt = (n) =>
  `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtN = (n, dec = 2) =>
  (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: dec, maximumFractionDigits: dec });
const pct = (n) => `${(parseFloat(n) || 0).toFixed(2)}%`;

const MONTHS = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const achievementColor = (p) => {
  const v = parseFloat(p) || 0;
  if (v >= 100) return ACCENT_GREEN;
  if (v >= 75)  return '#f57f17';
  return ACCENT_RED;
};

const achievementBg = (p) => {
  const v = parseFloat(p) || 0;
  if (v >= 100) return '#e8f5e9';
  if (v >= 75)  return '#fff8e1';
  return '#ffebee';
};

const achievementLabel = (p) => {
  const v = parseFloat(p) || 0;
  if (v >= 100) return '🏆 Achieved';
  if (v >= 75)  return '📈 On Track';
  return '⚠ Below Target';
};

const iconBtn = {
  border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
  height: 29, width: 29, color: '#fff', flexShrink: 0,
  '&:hover': { background: 'rgba(255,255,255,0.15)' },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

// ─── Column definitions ─────────────────────────────────────────────────────
const COLUMNS = [
  { key: '#',                    label: '#',                  align: 'center', width: 36  },
  { key: 'StaffName',            label: 'Staff Name',         align: 'left',   width: 160 },
  { key: 'BranchName',           label: 'Branch',             align: 'left',   width: 130 },
  { key: 'TargetAmount',         label: 'Target Amt',         align: 'right',  width: 120 },
  { key: 'AchievedAmount',       label: 'Achieved Amt',       align: 'right',  width: 120 },
  { key: 'BalanceAmount',        label: 'Balance Amt',        align: 'right',  width: 120 },
  { key: 'AchievementPercentage',label: 'Achievement %',      align: 'right',  width: 110 },
  { key: 'SoldQuantity',         label: 'Sold Qty',           align: 'right',  width: 90  },
  { key: 'InvoiceCount',         label: 'Invoices',           align: 'right',  width: 80  },
  { key: 'AverageInvoiceValue',  label: 'Avg Invoice Value',  align: 'right',  width: 130 },
  { key: 'Status',               label: 'Status',             align: 'center', width: 110 },
];

// ─── Main Component ──────────────────────────────────────────────────────────
const StaffTargetReport = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const today = new Date();
  const [month,       setMonth]       = useState(today.getMonth() + 1);
  const [year,        setYear]        = useState(today.getFullYear());
  const [data,        setData]        = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [fetched,     setFetched]     = useState(false);
  const [search,      setSearch]      = useState('');

  // ── Dropdown lists ──
  const [branches,  setBranches]  = useState([]);
  const [staffList, setStaffList] = useState([]);

  // ── Selected filters ──
  const [selBranch, setSelBranch] = useState('');
  const [selStaff,  setSelStaff]  = useState('');

  // ─── Load master dropdowns ─────────────────────────────────────────────────
  useEffect(() => {
    if (!companyId || !token) return;
    Promise.allSettled([
      fetchData('BranchApi',         `/${companyId}`, token),
      fetchData('SalesExecutiveApi', `/${companyId}`, token),
    ]).then(([brRes, stRes]) => {
      if (brRes.status === 'fulfilled') setBranches (brRes.value?.response_data || brRes.value || []);
      if (stRes.status === 'fulfilled') setStaffList(stRes.value?.response_data || stRes.value || []);
    });
  }, [companyId, token]);

  // ── Set default branch ──────────────────────────────────────────────────────
  useEffect(() => {
    if (branchId && !selBranch) setSelBranch(String(branchId));
  }, [branchId]); // eslint-disable-line

  // ─── Auto-fetch on auth ready ─────────────────────────────────────────────
  useEffect(() => {
    if (companyId && branchId && token) fetchReport();
  }, [companyId, branchId, token]); // eslint-disable-line

  // ─── Fetch report ─────────────────────────────────────────────────────────
  const fetchReport = async () => {
    if (!companyId || !token) { setError('Authentication error'); return; }
    const branchParam = selBranch ? parseInt(selBranch) : (branchId ? parseInt(branchId) : 0);
    const staffParam  = selStaff  ? parseInt(selStaff)  : 0;

    setLoading(true); setError('');
    try {
      const res = await fetchData(
        'ReportApi',
        `/StaffTargetAchievement/${companyId}/${branchParam}/${month}/${year}/${staffParam}`,
        token
      );
      const rows = res?.response_data ?? (Array.isArray(res) ? res : []);
      setData(Array.isArray(rows) ? rows : []);
      setFetched(true);
    } catch (err) {
      console.error('StaffTargetReport error:', err);
      setError(`Failed to fetch report: ${err?.response?.status ? `HTTP ${err.response.status}` : err?.message || 'Unknown error'}`);
      setFetched(true);
    } finally {
      setLoading(false);
    }
  };

  // ─── Client-side search filter ────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter(r =>
      (r.StaffName  || '').toLowerCase().includes(q) ||
      (r.BranchName || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  // ─── Summary totals ───────────────────────────────────────────────────────
  const totals = useMemo(() => filtered.reduce((acc, r) => ({
    TargetAmount:          acc.TargetAmount          + (parseFloat(r.TargetAmount)          || 0),
    AchievedAmount:        acc.AchievedAmount        + (parseFloat(r.AchievedAmount)        || 0),
    BalanceAmount:         acc.BalanceAmount          + (parseFloat(r.BalanceAmount)          || 0),
    SoldQuantity:          acc.SoldQuantity          + (parseFloat(r.SoldQuantity)          || 0),
    InvoiceCount:          acc.InvoiceCount          + (parseInt(r.InvoiceCount)            || 0),
  }), { TargetAmount: 0, AchievedAmount: 0, BalanceAmount: 0, SoldQuantity: 0, InvoiceCount: 0 }),
  [filtered]);

  const overallPct = totals.TargetAmount
    ? ((totals.AchievedAmount / totals.TargetAmount) * 100).toFixed(2)
    : '0.00';
  const overallAvgInvoice = totals.InvoiceCount
    ? totals.AchievedAmount / totals.InvoiceCount
    : 0;
  const isOverallAchieved = parseFloat(overallPct) >= 100;

  // ─── Group by branch ─────────────────────────────────────────────────────
  const groupedByBranch = useMemo(() => {
    const map = new Map();
    filtered.forEach(r => {
      const key = r.BranchName || 'Unknown Branch';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    return Array.from(map.entries());
  }, [filtered]);

  const branchSubtotal = (rows) => rows.reduce((acc, r) => ({
    TargetAmount:   acc.TargetAmount   + (parseFloat(r.TargetAmount)   || 0),
    AchievedAmount: acc.AchievedAmount + (parseFloat(r.AchievedAmount) || 0),
    BalanceAmount:  acc.BalanceAmount  + (parseFloat(r.BalanceAmount)  || 0),
    SoldQuantity:   acc.SoldQuantity   + (parseFloat(r.SoldQuantity)   || 0),
    InvoiceCount:   acc.InvoiceCount   + (parseInt(r.InvoiceCount)     || 0),
  }), { TargetAmount: 0, AchievedAmount: 0, BalanceAmount: 0, SoldQuantity: 0, InvoiceCount: 0 });

  // ─── Excel Export ─────────────────────────────────────────────────────────
  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'StaffTargetReport', 0);
    if (!filtered.length) return;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Staff Target Report');
    const totalCols = COLUMNS.length;
    ws.views = [{ state: 'frozen', xSplit: 0, ySplit: 5 }];
    const thin = { top:{style:'thin'}, left:{style:'thin'}, bottom:{style:'thin'}, right:{style:'thin'} };

    // Header rows
    const addMergedRow = (text, font, fill, height) => {
      ws.mergeCells(ws.rowCount + 1, 1, ws.rowCount + 1, totalCols);
      const cell = ws.getCell(`A${ws.rowCount}`);
      cell.value = text; cell.font = font;
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      if (fill) cell.fill = fill;
      ws.getRow(ws.rowCount).height = height || 18;
    };

    addMergedRow(companyName || 'Company',
      { bold:true, size:14, color:{argb:'FF1a237e'} },
      { type:'pattern', pattern:'solid', fgColor:{argb:'FFe8eaf6'} }, 22);
    addMergedRow('Staff Target Achievement Report',
      { bold:true, size:12, color:{argb:'FF283593'} }, null, 18);
    addMergedRow(`Period: ${MONTHS[month]} ${year}`,
      { size:10, color:{argb:'FF37474f'} }, null, 16);
    addMergedRow(`Generated on: ${new Date().toLocaleString()} | By: ${username}`,
      { italic:true, size:9, color:{argb:'FF9e9e9e'} }, null, 14);

    const hdr = ws.addRow(COLUMNS.map(c => c.label));
    hdr.height = 18;
    hdr.eachCell((cell, ci) => {
      cell.font      = { bold:true, size:9, color:{argb:'FFFFFFFF'} };
      cell.fill      = { type:'pattern', pattern:'solid', fgColor:{argb:'FF1a237e'} };
      cell.alignment = { horizontal: COLUMNS[ci-1]?.align === 'right' ? 'right' : 'center', vertical:'middle' };
      cell.border    = thin;
    });

    let globalIdx = 0;
    groupedByBranch.forEach(([branchName, branchRows]) => {
      // Branch header
      const bHdr = ws.addRow(COLUMNS.map((_, ci) => ci === 0 ? '' : ci === 1 ? `  ${branchName}` : ''));
      bHdr.height = 16;
      bHdr.eachCell(cell => {
        cell.font  = { bold:true, size:10, color:{argb:'FFFFFFFF'} };
        cell.fill  = { type:'pattern', pattern:'solid', fgColor:{argb:'FF283593'} };
        cell.border = thin;
        cell.alignment = { horizontal:'left', vertical:'middle' };
      });
      if (totalCols > 2) ws.mergeCells(bHdr.number, 2, bHdr.number, totalCols);

      branchRows.forEach((r, i) => {
        globalIdx++;
        const achPct = parseFloat(r.AchievementPercentage) || 0;
        const row = ws.addRow(COLUMNS.map(c => {
          if (c.key === '#')                     return globalIdx;
          if (c.key === 'TargetAmount')          return parseFloat(r.TargetAmount)          || 0;
          if (c.key === 'AchievedAmount')        return parseFloat(r.AchievedAmount)        || 0;
          if (c.key === 'BalanceAmount')         return parseFloat(r.BalanceAmount)         || 0;
          if (c.key === 'AchievementPercentage') return `${achPct.toFixed(2)}%`;
          if (c.key === 'SoldQuantity')          return parseFloat(r.SoldQuantity)          || 0;
          if (c.key === 'InvoiceCount')          return parseInt(r.InvoiceCount)            || 0;
          if (c.key === 'AverageInvoiceValue')   return parseFloat(r.AverageInvoiceValue)   || 0;
          if (c.key === 'Status')                return achievementLabel(achPct).replace(/[🏆📈⚠]/g,'').trim();
          return r[c.key] ?? '';
        }));
        const bg = i % 2 === 0 ? 'FFFFFFFF' : 'FFf3f4fd';
        row.eachCell((cell, ci) => {
          cell.border    = thin;
          cell.fill      = { type:'pattern', pattern:'solid', fgColor:{argb:bg} };
          cell.alignment = { horizontal: COLUMNS[ci-1]?.align || 'left', vertical:'middle' };
          cell.font      = { size:9 };
        });
      });

      // Branch subtotal
      const sub = branchSubtotal(branchRows);
      const subPct = sub.TargetAmount ? ((sub.AchievedAmount / sub.TargetAmount) * 100).toFixed(2) : '0.00';
      const stRow = ws.addRow(COLUMNS.map(c => {
        if (c.key === '#')               return '';
        if (c.key === 'StaffName')       return `Subtotal — ${branchName}`;
        if (c.key === 'TargetAmount')    return parseFloat(sub.TargetAmount)   || 0;
        if (c.key === 'AchievedAmount')  return parseFloat(sub.AchievedAmount) || 0;
        if (c.key === 'BalanceAmount')   return parseFloat(sub.BalanceAmount)  || 0;
        if (c.key === 'AchievementPercentage') return `${subPct}%`;
        if (c.key === 'SoldQuantity')    return parseFloat(sub.SoldQuantity)   || 0;
        if (c.key === 'InvoiceCount')    return parseInt(sub.InvoiceCount)     || 0;
        return '';
      }));
      stRow.eachCell((cell, ci) => {
        cell.font   = { bold:true, size:9, color:{argb:'FF1a237e'} };
        cell.fill   = { type:'pattern', pattern:'solid', fgColor:{argb:'FFe8eaf6'} };
        cell.border = thin;
        cell.alignment = { horizontal: COLUMNS[ci-1]?.align || 'left', vertical:'middle' };
      });
    });

    // Grand total
    const grandPct = totals.TargetAmount ? ((totals.AchievedAmount / totals.TargetAmount) * 100).toFixed(2) : '0.00';
    const gtRow = ws.addRow(COLUMNS.map(c => {
      if (c.key === '#')               return '';
      if (c.key === 'StaffName')       return 'GRAND TOTAL';
      if (c.key === 'TargetAmount')    return parseFloat(totals.TargetAmount)   || 0;
      if (c.key === 'AchievedAmount')  return parseFloat(totals.AchievedAmount) || 0;
      if (c.key === 'BalanceAmount')   return parseFloat(totals.BalanceAmount)  || 0;
      if (c.key === 'AchievementPercentage') return `${grandPct}%`;
      if (c.key === 'SoldQuantity')    return parseFloat(totals.SoldQuantity)   || 0;
      if (c.key === 'InvoiceCount')    return parseInt(totals.InvoiceCount)     || 0;
      return '';
    }));
    gtRow.eachCell((cell, ci) => {
      cell.font   = { bold:true, size:10, color:{argb:'FFFFFFFF'} };
      cell.fill   = { type:'pattern', pattern:'solid', fgColor:{argb:'FF0d47a1'} };
      cell.border = thin;
      cell.alignment = { horizontal: COLUMNS[ci-1]?.align || 'left', vertical:'middle' };
    });

    ws.columns = COLUMNS.map(c => ({ width: Math.max((c.width || 100) / 7, 8) }));
    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf]), `StaffTargetReport_${MONTHS[month]}_${year}.xlsx`);
  };

  // ─── Cell renderer ────────────────────────────────────────────────────────
  const renderCell = (col, row) => {
    const v = row[col.key];
    switch (col.key) {
      case 'TargetAmount':
      case 'AchievedAmount':
      case 'AverageInvoiceValue': return fmtAmt(v);
      case 'BalanceAmount':
        return (
          <span style={{ color: (parseFloat(v)||0) <= 0 ? ACCENT_GREEN : ACCENT_RED, fontWeight: 700 }}>
            {fmtAmt(v)}
          </span>
        );
      case 'AchievementPercentage': {
        const p = parseFloat(v) || 0;
        return (
          <div style={{ display:'flex', alignItems:'center', gap:4 }}>
            <div style={{ flex:1, minWidth:40 }}>
              <LinearProgress
                variant="determinate"
                value={Math.min(p, 100)}
                sx={{
                  height: 6, borderRadius: 3,
                  backgroundColor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: achievementColor(p),
                    borderRadius: 3,
                  },
                }}
              />
            </div>
            <span style={{ color: achievementColor(p), fontWeight: 700, fontSize: 10, whiteSpace:'nowrap' }}>
              {pct(v)}
            </span>
          </div>
        );
      }
      case 'SoldQuantity':  return fmtN(v, 2);
      case 'InvoiceCount':  return (parseInt(v) || 0).toLocaleString('en-IN');
      case 'Status': {
        const p = parseFloat(row.AchievementPercentage) || 0;
        return (
          <span style={{
            display:'inline-block', padding:'2px 8px', borderRadius:10, fontSize:10, fontWeight:700,
            background: achievementBg(p), color: achievementColor(p), whiteSpace:'nowrap',
          }}>
            {achievementLabel(p)}
          </span>
        );
      }
      default: return v ?? '-';
    }
  };

  // ─── Styles ───────────────────────────────────────────────────────────────
  const thStyle = (col) => ({
    position:'sticky', top:0, zIndex:3,
    background: PRIMARY_DARK, color:'#fff',
    fontWeight:700, fontSize:10, textTransform:'uppercase', letterSpacing:'0.4px',
    padding:'7px 8px', borderRight:'1px solid rgba(255,255,255,0.12)',
    borderBottom:'2px solid rgba(255,255,255,0.2)',
    whiteSpace:'nowrap', textAlign: col.align,
    minWidth: col.width, width: col.width,
  });
  const tdStyle = (col, i) => ({
    padding:'5px 8px', fontSize:11, textAlign: col.align,
    borderBottom:'1px solid #f1f5f9', borderRight:'1px solid #f1f5f9',
    whiteSpace:'nowrap', color:'#374151',
    background: i % 2 === 0 ? '#ffffff' : '#f3f4fd',
  });

  // ─── KPI cards ────────────────────────────────────────────────────────────
  const kpis = [
    { label:'Staff',          value: filtered.length,                    color: PRIMARY_MID,                              bg:'#e8eaf6' },
    { label:'Target Amt',     value: fmtAmt(totals.TargetAmount),        color:'#0277bd',                                 bg:'#e1f5fe' },
    { label:'Achieved Amt',   value: fmtAmt(totals.AchievedAmount),      color: isOverallAchieved ? ACCENT_GREEN : ACCENT_AMBER, bg: isOverallAchieved ? '#e8f5e9' : '#fff3e0' },
    { label:'Balance Amt',    value: fmtAmt(totals.BalanceAmount),       color: totals.BalanceAmount <= 0 ? ACCENT_GREEN : ACCENT_RED, bg: totals.BalanceAmount <= 0 ? '#e8f5e9' : '#ffebee' },
    { label:'Achievement %',  value: `${overallPct}%`,                   color: achievementColor(overallPct),             bg: achievementBg(overallPct) },
    { label:'Sold Qty',       value: fmtN(totals.SoldQuantity, 2),       color:'#6a1b9a',                                 bg:'#f3e5f5' },
    { label:'Invoices',       value: totals.InvoiceCount.toLocaleString('en-IN'), color:'#004d40',                        bg:'#e0f2f1' },
    { label:'Avg Invoice',    value: fmtAmt(overallAvgInvoice),          color:'#558b2f',                                 bg:'#f1f8e9' },
  ];

  // ─── Dropdown options ─────────────────────────────────────────────────────
  const branchOpts = [{ value:'', label:'— All Branches —' }, ...branches.map(b => ({ value:String(b.Id), label: b.Name || b.BranchName || '' }))];
  const staffOpts  = [{ value:'', label:'— All Staff —' },    ...staffList.map(s => ({ value:String(s.Id), label: s.Name || '' }))];

  const currentYear  = today.getFullYear();
  const yearOptions  = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight:'100%', background:'#f0f2f5', padding:10, fontFamily:"'Inter','Segoe UI',sans-serif", boxSizing:'border-box' }}>
      <LogoLoader open={loading} message="Loading Staff Target Report..." />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical:'top', horizontal:'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width:'100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background:'#fff', borderRadius:10, boxShadow:'0 4px 16px rgba(0,0,0,0.09)', border:'1px solid #e2e8f0', overflow:'hidden' }}>

        {/* ── Toolbar ── */}
        <div style={{
          display:'flex', alignItems:'center', padding:'6px 12px',
          background:`linear-gradient(135deg, ${PRIMARY_DARK} 0%, ${PRIMARY} 60%, ${PRIMARY_MID} 100%)`,
          gap:6, flexWrap:'nowrap', overflowX:'auto', overflowY:'hidden', scrollbarWidth:'none',
          boxShadow:'0 2px 8px rgba(13,71,161,0.35)',
        }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
              <ArrowBack sx={{ fontSize:16 }} />
            </IconButton>
          </Tooltip>

          <div style={{ flexShrink:0 }}>
            <div style={{ fontSize:13, fontWeight:800, color:'#fff', whiteSpace:'nowrap', letterSpacing:'0.3px' }}>Staff Target Report</div>
            <div style={{ fontSize:9, color:'rgba(255,255,255,0.65)', whiteSpace:'nowrap' }}>Monthly target achievement by staff</div>
          </div>

          <div style={{ flex:1, minWidth:4 }} />

          {/* Search */}
          <input
            placeholder="Search staff / branch..."
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ height:29, border:'1px solid rgba(255,255,255,0.3)', borderRadius:8, padding:'0 10px',
              fontSize:12, backgroundColor:'rgba(255,255,255,0.1)', color:'#fff', colorScheme:'dark',
              flexShrink:0, minWidth:160, outline:'none' }}
          />

          {/* Branch filter */}
          <SearchableDropdown value={selBranch} onChange={v => setSelBranch(v)}
            options={branchOpts} placeholder="— Branch —" minWidth={0} primaryColor={PRIMARY}
          />

          {/* Staff filter */}
          <SearchableDropdown value={selStaff} onChange={v => setSelStaff(v)}
            options={staffOpts} placeholder="— Staff —" minWidth={0} primaryColor={PRIMARY}
          />

          {/* Month */}
          <select value={month} onChange={e => setMonth(parseInt(e.target.value))}
            style={{ height:29, border:'1px solid rgba(255,255,255,0.3)', borderRadius:8, padding:'0 8px',
              fontSize:12, backgroundColor:'rgba(255,255,255,0.1)', color:'#fff', colorScheme:'dark',
              flexShrink:0, outline:'none', cursor:'pointer' }}>
            {MONTHS.slice(1).map((m, i) => (
              <option key={i+1} value={i+1} style={{ background:'#1a237e', color:'#fff' }}>{m}</option>
            ))}
          </select>

          {/* Year */}
          <select value={year} onChange={e => setYear(parseInt(e.target.value))}
            style={{ height:29, border:'1px solid rgba(255,255,255,0.3)', borderRadius:8, padding:'0 8px',
              fontSize:12, backgroundColor:'rgba(255,255,255,0.1)', color:'#fff', colorScheme:'dark',
              flexShrink:0, outline:'none', cursor:'pointer' }}>
            {yearOptions.map(y => (
              <option key={y} value={y} style={{ background:'#1a237e', color:'#fff' }}>{y}</option>
            ))}
          </select>

          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
              <Refresh sx={{ fontSize:16 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
              <GetApp sx={{ fontSize:16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* ── KPI Strip ── */}
        {fetched && filtered.length > 0 && (
          <div style={{ display:'flex', flexWrap:'nowrap', gap:0, borderBottom:'1px solid #e8eaf6', overflowX:'auto', scrollbarWidth:'none' }}>
            {kpis.map((k, i) => (
              <div key={k.label} style={{
                flex:1, minWidth:90, padding:'7px 10px', textAlign:'center',
                background: k.bg, borderRight: i < kpis.length-1 ? '1px solid #e8eaf6' : 'none',
              }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:3 }}>
                  {k.label === 'Achievement %' && (
                    parseFloat(overallPct) >= 100
                      ? <EmojiEvents sx={{ fontSize:12, color:k.color }} />
                      : parseFloat(overallPct) >= 75
                        ? <TrendingUp sx={{ fontSize:12, color:k.color }} />
                        : <TrendingDown sx={{ fontSize:12, color:k.color }} />
                  )}
                  <div style={{ fontSize:9, color:'#78909c', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.3px' }}>{k.label}</div>
                </div>
                <div style={{ fontSize:13, fontWeight:800, color:k.color, lineHeight:1.3, whiteSpace:'nowrap' }}>{k.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* ── Meta row ── */}
        {fetched && (
          <div style={{ padding:'4px 14px', fontSize:11, color:'#64748b', borderBottom:'1px solid #f1f5f9', display:'flex', alignItems:'center', gap:12 }}>
            <span>{MONTHS[month]} {year}</span>
            <span>·</span>
            <span><strong>{filtered.length}</strong> staff member{filtered.length !== 1 ? 's' : ''}{search && ` (filtered from ${data.length})`}</span>
            {selBranch && <><span>·</span><span>Branch: <strong>{branches.find(b => String(b.Id) === selBranch)?.Name || selBranch}</strong></span></>}
          </div>
        )}

        {/* ── Data Table ── */}
        <div style={{
          overflowX:'auto', overflowY:'auto',
          maxHeight:'calc(100vh - 250px)',
          position:'relative', scrollbarWidth:'thin',
          scrollbarColor:`${PRIMARY_MID} #e8eaf6`,
        }}>
          <table style={{ borderCollapse:'collapse', fontSize:11, minWidth:1050, width:'100%' }}>
            <thead>
              <tr>{COLUMNS.map(col => <th key={col.key} style={thStyle(col)}>{col.label}</th>)}</tr>
            </thead>

            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length} style={{ textAlign:'center', padding:48, color:'#94a3b8', fontSize:14 }}>
                    {!fetched ? 'Select month/year and click Refresh to load data' : 'No records found for the selected criteria'}
                  </td>
                </tr>
              ) : (() => {
                let globalIdx = 0;
                return groupedByBranch.map(([branchName, branchRows]) => {
                  const sub = branchSubtotal(branchRows);
                  const subPct = sub.TargetAmount ? ((sub.AchievedAmount / sub.TargetAmount) * 100).toFixed(2) : '0.00';
                  const subIsAchieved = parseFloat(subPct) >= 100;
                  return (
                    <React.Fragment key={branchName}>
                      {/* Branch header */}
                      <tr>
                        <td colSpan={COLUMNS.length} style={{
                          background:'linear-gradient(90deg, #1a237e 0%, #283593 100%)',
                          color:'#fff', fontWeight:700, fontSize:11,
                          padding:'5px 12px', letterSpacing:'0.3px',
                          borderTop:'2px solid #3949ab', borderBottom:'1px solid rgba(255,255,255,0.15)',
                        }}>
                          🏢 {branchName}
                          <span style={{ marginLeft:10, fontSize:9, fontWeight:400, background:'rgba(255,255,255,0.18)', borderRadius:4, padding:'1px 7px', verticalAlign:'middle' }}>
                            {branchRows.length} staff member{branchRows.length !== 1 ? 's' : ''}
                          </span>
                        </td>
                      </tr>

                      {/* Data rows */}
                      {branchRows.map((row, i) => {
                        const rowIdx = globalIdx++;
                        return (
                          <tr key={`${row.StaffId}-${rowIdx}`}
                            onMouseEnter={e => { e.currentTarget.style.background = '#eef2ff'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = i % 2 === 0 ? '#ffffff' : '#f3f4fd'; }}
                          >
                            {COLUMNS.map(col => (
                              <td key={col.key} style={tdStyle(col, i)}>
                                {col.key === '#' ? rowIdx + 1 : renderCell(col, row)}
                              </td>
                            ))}
                          </tr>
                        );
                      })}

                      {/* Branch subtotal */}
                      <tr>
                        {COLUMNS.map(col => {
                          const stStyle = {
                            background:'#e8eaf6', color:'#1a237e', fontWeight:700, fontSize:10,
                            padding:'5px 8px', whiteSpace:'nowrap',
                            borderTop:'1px solid #c5cae9', borderBottom:'2px solid #9fa8da',
                            borderRight:'1px solid #c5cae9', textAlign: col.align,
                          };
                          if (col.key === '#')                     return <td key={col.key} style={stStyle}></td>;
                          if (col.key === 'StaffName')             return <td key={col.key} style={{...stStyle,textAlign:'right',color:'#37474f'}}>Subtotal</td>;
                          if (col.key === 'BranchName')            return <td key={col.key} style={{...stStyle, color:'#37474f', fontStyle:'italic'}}>{branchName}</td>;
                          if (col.key === 'TargetAmount')          return <td key={col.key} style={stStyle}>{fmtAmt(sub.TargetAmount)}</td>;
                          if (col.key === 'AchievedAmount')        return <td key={col.key} style={{...stStyle,color: subIsAchieved ? ACCENT_GREEN : ACCENT_AMBER}}>{fmtAmt(sub.AchievedAmount)}</td>;
                          if (col.key === 'BalanceAmount')         return <td key={col.key} style={{...stStyle,color: sub.BalanceAmount <= 0 ? ACCENT_GREEN : ACCENT_RED}}>{fmtAmt(sub.BalanceAmount)}</td>;
                          if (col.key === 'AchievementPercentage') return <td key={col.key} style={{...stStyle,color: achievementColor(subPct)}}>{subPct}%</td>;
                          if (col.key === 'SoldQuantity')          return <td key={col.key} style={stStyle}>{fmtN(sub.SoldQuantity, 2)}</td>;
                          if (col.key === 'InvoiceCount')          return <td key={col.key} style={stStyle}>{sub.InvoiceCount.toLocaleString('en-IN')}</td>;
                          if (col.key === 'AverageInvoiceValue')   return <td key={col.key} style={stStyle}>{fmtAmt(sub.InvoiceCount ? sub.AchievedAmount / sub.InvoiceCount : 0)}</td>;
                          if (col.key === 'Status')                return (
                            <td key={col.key} style={{...stStyle, textAlign:'center'}}>
                              <span style={{ display:'inline-block', padding:'2px 8px', borderRadius:10, fontSize:10, fontWeight:700, background: achievementBg(subPct), color: achievementColor(subPct), whiteSpace:'nowrap' }}>
                                {achievementLabel(subPct)}
                              </span>
                            </td>
                          );
                          return <td key={col.key} style={stStyle}></td>;
                        })}
                      </tr>
                    </React.Fragment>
                  );
                });
              })()}
            </tbody>

            {/* Sticky grand-total footer */}
            {filtered.length > 0 && (
              <tfoot>
                <tr>
                  {COLUMNS.map(col => {
                    const tfStyle = {
                      position:'sticky', bottom:0, zIndex:2,
                      background:`linear-gradient(135deg, ${PRIMARY_DARK} 0%, ${PRIMARY_MID} 100%)`,
                      color:'#fff', fontWeight:700, fontSize:10,
                      padding:'6px 8px', borderTop:'2px solid rgba(255,255,255,0.2)',
                      borderRight:'1px solid rgba(255,255,255,0.1)',
                      textAlign: col.align, whiteSpace:'nowrap',
                    };
                    if (col.key === '#')                     return <td key={col.key} style={tfStyle}></td>;
                    if (col.key === 'StaffName')             return <td key={col.key} style={{...tfStyle,textAlign:'right',fontSize:11}}>TOTALS</td>;
                    if (col.key === 'BranchName')            return <td key={col.key} style={{...tfStyle, fontStyle:'italic', color:'rgba(255,255,255,0.7)'}}>{filtered.length} Staff · {groupedByBranch.length} Branch{groupedByBranch.length !== 1 ? 'es' : ''}</td>;
                    if (col.key === 'TargetAmount')          return <td key={col.key} style={{...tfStyle,color:'#b3e5fc'}}>{fmtAmt(totals.TargetAmount)}</td>;
                    if (col.key === 'AchievedAmount')        return <td key={col.key} style={{...tfStyle,color: isOverallAchieved ? '#a5d6a7' : '#ffe082'}}>{fmtAmt(totals.AchievedAmount)}</td>;
                    if (col.key === 'BalanceAmount')         return <td key={col.key} style={{...tfStyle,color: totals.BalanceAmount <= 0 ? '#a5d6a7' : '#ef9a9a'}}>{fmtAmt(totals.BalanceAmount)}</td>;
                    if (col.key === 'AchievementPercentage') return <td key={col.key} style={{...tfStyle,color: isOverallAchieved ? '#a5d6a7' : '#ffe082'}}>{overallPct}%</td>;
                    if (col.key === 'SoldQuantity')          return <td key={col.key} style={{...tfStyle,color:'#ce93d8'}}>{fmtN(totals.SoldQuantity, 2)}</td>;
                    if (col.key === 'InvoiceCount')          return <td key={col.key} style={{...tfStyle,color:'#b2dfdb'}}>{totals.InvoiceCount.toLocaleString('en-IN')}</td>;
                    if (col.key === 'AverageInvoiceValue')   return <td key={col.key} style={{...tfStyle,color:'#c8e6c9'}}>{fmtAmt(overallAvgInvoice)}</td>;
                    if (col.key === 'Status')                return (
                      <td key={col.key} style={{...tfStyle, textAlign:'center'}}>
                        <span style={{ display:'inline-block', padding:'2px 8px', borderRadius:10, fontSize:10, fontWeight:700, background:'rgba(255,255,255,0.15)', color: isOverallAchieved ? '#a5d6a7' : '#ffe082', whiteSpace:'nowrap', border:'1px solid rgba(255,255,255,0.25)' }}>
                          {achievementLabel(overallPct)}
                        </span>
                      </td>
                    );
                    return <td key={col.key} style={tfStyle}></td>;
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        </div>

      </div>
    </div>
  );
};

export default StaffTargetReport;
